import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Clock, Music, Wind, Brain, Check, Star, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const GOLD = "#d4af37";

type DigitalItem = {
  id: string;
  title?: string;
  baslik?: string;
  description?: string;
  aciklama?: string;
  duration_minutes?: number;
  sure_saniye?: number;
  cover_image_url?: string;
  gorsel_url?: string;
  price?: number;
  tags?: string[];
};

type Review = {
  id: string;
  display_name: string | null;
  rating: number;
  comment: string;
  created_at: string;
};

type ContentType = "meditation" | "meditasyon" | "breath" | "nefes" | "frekans" | "diyet" | "rituel";

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  meditation: "Meditasyon",
  meditasyon: "Meditasyon",
  breath: "Nefes Egzersizi",
  nefes: "Nefes Egzersizi",
  frekans: "Frekans Seti",
  diyet: "Diyet Programı",
  rituel: "Ritüel",
};

const CONTENT_TYPE_ICONS: Record<ContentType, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  meditation: Brain,
  meditasyon: Brain,
  breath: Wind,
  nefes: Wind,
  frekans: Music,
  diyet: Star,
  rituel: Star,
};

const CONTENT_TYPE_FEATURES: Record<ContentType, string[]> = {
  meditation: [
    "Rehberli meditasyon ses kaydı",
    "Ömür boyu erişim — bir kez satın al, sonsuza kadar dinle",
    "Soul Circles uygulamasından offline erişim",
    "Düzenli pratik için favori listesine ekle",
  ],
  meditasyon: [
    "Rehberli meditasyon ses kaydı",
    "Ömür boyu erişim — bir kez satın al, sonsuza kadar dinle",
    "Soul Circles uygulamasından offline erişim",
    "Düzenli pratik için favori listesine ekle",
  ],
  breath: [
    "Adım adım nefes rehberi ses kaydı",
    "Farklı seanslar için optimize edilmiş süre",
    "Soul Circles uygulamasından istediğin zaman eriş",
    "Stres azaltma ve enerji dengeleme odaklı",
  ],
  nefes: [
    "Adım adım nefes rehberi ses kaydı",
    "Farklı seanslar için optimize edilmiş süre",
    "Soul Circles uygulamasından istediğin zaman eriş",
    "Stres azaltma ve enerji dengeleme odaklı",
  ],
  frekans: [
    "Yüksek kaliteli frekans müziği seti",
    "Chakra veya bilinç düzeyi ile uyumlu frekanslar",
    "Meditasyon, uyku veya odaklanma için ideal",
    "Soul Circles uygulamasından offline erişim",
  ],
  diyet: [
    "Numerolojik analiz ile kişiselleştirilmiş beslenme planı",
    "Ömür boyu erişim — bir kez satın al, sonsuza kadar kullan",
    "Soul Circles uygulamasından günlük takip",
    "Su hedefi ve öğün zamanlaması rehberi",
  ],
  rituel: [
    "Adım adım uygulama rehberi ve malzeme listesi",
    "Ömür boyu erişim — bir kez satın al, sonsuza kadar uygula",
    "Soul Circles uygulamasından istediğin zaman eriş",
    "Niyet belirleme ve enerji yönlendirme pratiği",
  ],
};

function formatTry(n: number) {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n} ₺`;
  }
}

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "long", year: "numeric" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const TAG_PREFIXES = [
  "rituel_niyet:", "rituel_adim:", "rituel_malzeme:", "rituel_puan:",
  "rituel_program_gun:", "rituel_saat:", "gorev_tur:", "program_gun:", "xp:",
];

function isInternalTag(tag: string) {
  return TAG_PREFIXES.some((p) => tag.startsWith(p));
}

function cleanDescription(text: string): string {
  return text
    .split("\n")
    .filter((line) => !TAG_PREFIXES.some((p) => line.trimStart().startsWith(p)))
    .join("\n")
    .trim();
}

function formatDuration(item: DigitalItem): string | null {
  if (item.duration_minutes) return `${item.duration_minutes} dakika`;
  if (item.sure_saniye) return `${Math.round(item.sure_saniye / 60)} dakika`;
  return null;
}

function Stars({ rating, interactive, onSelect, className }: {
  rating: number;
  interactive?: boolean;
  onSelect?: (v: number) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {Array.from({ length: 5 }).map((_, i) => {
        const val = i + 1;
        const active = val <= rating;
        return (
          <button
            key={val}
            type="button"
            disabled={!interactive}
            onClick={() => onSelect?.(val)}
            className={cn("transition-transform", interactive && "hover:scale-110 cursor-pointer", !interactive && "cursor-default")}
            aria-label={`${val} yıldız`}
          >
            <Star
              className={cn("h-4 w-4", active ? "fill-current" : "fill-transparent")}
              style={{ color: active ? GOLD : "rgba(212,175,55,0.25)" }}
            />
          </button>
        );
      })}
    </div>
  );
}

async function fetchCatalog() {
  const r = await fetch("/api/numeroloji/catalog");
  if (!r.ok) throw new Error("Katalog alınamadı.");
  return r.json();
}

async function fetchReviews(contentType: string, contentId: string): Promise<Review[]> {
  const r = await fetch(`/api/numeroloji/content-reviews?contentType=${contentType}&contentId=${contentId}`);
  const json = await r.json().catch(() => null);
  if (!r.ok) throw new Error(json?.error ?? "Yorumlar alınamadı.");
  return Array.isArray(json?.reviews) ? json.reviews : [];
}

async function submitReview(body: {
  contentType: string;
  contentId: string;
  displayName: string;
  rating: number;
  comment: string;
}) {
  const r = await fetch("/api/numeroloji/content-reviews", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await r.json().catch(() => null);
  if (!r.ok) throw new Error(json?.error ?? "Yorum gönderilemedi.");
}

export default function DigitalIcerikDetay() {
  const { contentType, id } = useParams<{ contentType: string; id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [inCart, setInCart] = useState(() => {
    const cart = JSON.parse(localStorage.getItem("numeroloji_cart_v1") ?? "[]");
    return cart.some((i: any) => i.digitalContent?.contentId === id);
  });

  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSent, setReviewSent] = useState<"ok" | string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ["catalog"],
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });

  const reviewsQuery = useQuery({
    queryKey: ["content-reviews", contentType, id],
    queryFn: () => fetchReviews(contentType!, id!),
    enabled: Boolean(contentType && id),
    staleTime: 30_000,
  });

  const reviewMutation = useMutation({
    mutationFn: submitReview,
    onSuccess: () => {
      setReviewSent("ok");
      setReviewName("");
      setReviewComment("");
      setReviewRating(5);
      queryClient.invalidateQueries({ queryKey: ["content-reviews", contentType, id] });
    },
    onError: (e: Error) => {
      setReviewSent(e.message);
    },
  });

  const dc = catalogQuery.data?.digitalContent;
  let item: DigitalItem | undefined;
  if (dc && id) {
    if (contentType === "meditation" || contentType === "meditasyon") item = dc.meditations?.find((x: DigitalItem) => x.id === id);
    else if (contentType === "breath" || contentType === "nefes") item = dc.breaths?.find((x: DigitalItem) => x.id === id);
    else if (contentType === "frekans") item = dc.frekans?.find((x: DigitalItem) => x.id === id);
    else if (contentType === "diyet") item = dc.diets?.find((x: DigitalItem) => x.id === id);
    else if (contentType === "rituel") item = dc.rituals?.find((x: DigitalItem) => x.id === id);
  }

  const ct = (contentType as ContentType) ?? "meditation";
  const TypeIcon = CONTENT_TYPE_ICONS[ct] ?? Brain;
  const title = item?.title ?? item?.baslik ?? "İçerik";
  const description = item?.description ?? item?.aciklama ?? "";
  const imageUrl = item?.cover_image_url ?? item?.gorsel_url ?? "";
  const price = item?.price ?? 0;
  const duration = item ? formatDuration(item) : null;
  const features = CONTENT_TYPE_FEATURES[ct];
  const reviews = reviewsQuery.data ?? [];
  const ratingAvg = reviews.length
    ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
    : 0;

  function handlePurchase() {
    if (inCart) {
      navigate("/numeroloji/sepet");
      return;
    }
    const cart = JSON.parse(localStorage.getItem("numeroloji_cart_v1") ?? "[]");
    const filtered = cart.filter((i: any) => !i.digitalContent);
    filtered.push({ digitalContent: { contentType: ct, contentId: id, price, title } });
    localStorage.setItem("numeroloji_cart_v1", JSON.stringify(filtered));
    setInCart(true);
  }

  function handleSubmitReview() {
    if (reviewMutation.isPending) return;
    setReviewSent(null);
    reviewMutation.mutate({
      contentType: ct,
      contentId: id!,
      displayName: reviewName.trim(),
      rating: reviewRating,
      comment: reviewComment.trim(),
    });
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg, #08080c 0%, #0e0e14 60%, #070709 100%)" }}>
      {/* Sticky nav */}
      <nav className="sticky top-0 z-30 border-b bg-black/40 backdrop-blur-sm" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <Button asChild variant="ghost" className="gap-2 text-slate-300 hover:text-white">
            <Link to="/numeroloji/magaza">
              <ArrowLeft className="h-4 w-4" />
              Mağaza
            </Link>
          </Button>
          <div className="font-display text-sm tracking-widest" style={{ color: GOLD }}>
            Numeroloji.app
          </div>
          <div className="w-24" />
        </div>
      </nav>

      <main className="mx-auto max-w-5xl px-4 py-12">
        {catalogQuery.isLoading ? (
          <div className="flex h-64 items-center justify-center text-slate-400">Yükleniyor...</div>
        ) : catalogQuery.isError ? (
          <div className="mx-auto mt-6 max-w-2xl rounded-xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-300">
            {(catalogQuery.error as Error).message}
          </div>
        ) : !item ? (
          <div className="flex h-64 flex-col items-center justify-center gap-4 text-slate-400">
            <div>İçerik bulunamadı.</div>
            <Button
              asChild
              variant="outline"
              className="border-[rgba(212,175,55,0.35)] bg-transparent text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
            >
              <Link to="/numeroloji/magaza">Mağazaya dön</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Hero grid */}
            <section className="grid gap-10 lg:grid-cols-[400px_1fr]">
              {/* Image */}
              <div className="relative">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full max-h-80 rounded-2xl object-contain shadow-2xl"
                    style={{ aspectRatio: "16/9", maxHeight: "320px" }}
                  />
                ) : (
                  <div
                    className="flex w-full max-h-80 items-center justify-center rounded-2xl"
                    style={{
                      aspectRatio: "16/9",
                      maxHeight: "320px",
                      background: "linear-gradient(135deg, #1e1e2e, #0e0e14)",
                      border: "1px solid rgba(212,175,55,0.15)",
                    }}
                  >
                    <TypeIcon className="h-24 w-24 opacity-30" style={{ color: GOLD }} />
                  </div>
                )}
                <span
                  className="absolute left-3 top-3 rounded-full px-3 py-1 text-[11px] font-semibold"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.55)",
                    border: "1px solid rgba(212,175,55,0.30)",
                    color: GOLD,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {CONTENT_TYPE_LABELS[ct]}
                </span>
              </div>

              {/* Info */}
              <div className="flex flex-col">
                <h1 className="font-display text-3xl tracking-tight text-white sm:text-4xl">{title}</h1>

                {/* Meta */}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {duration && (
                    <div className="flex items-center gap-1.5 text-sm text-slate-400">
                      <Clock className="h-4 w-4" />
                      {duration}
                    </div>
                  )}
                  {ratingAvg > 0 && (
                    <div className="flex items-center gap-1.5">
                      <Stars rating={Math.round(ratingAvg)} />
                      <span className="text-xs text-slate-400">({reviews.length})</span>
                    </div>
                  )}
                  {item.tags?.filter((tag) => !isInternalTag(tag)).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full px-3 py-0.5 text-xs"
                      style={{ backgroundColor: "rgba(212,175,55,0.10)", color: "rgba(212,175,55,0.80)" }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {description && (
                  <p className="mt-6 text-sm leading-relaxed text-slate-300/90">{cleanDescription(description)}</p>
                )}

                {/* Features */}
                <div className="mt-6 grid gap-3">
                  {features.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-sm">
                      <span
                        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: "rgba(212,175,55,0.15)" }}
                      >
                        <Check className="h-3 w-3" style={{ color: GOLD }} />
                      </span>
                      <span className="text-slate-200">{f}</span>
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="mt-8">
                  {price > 0 ? (
                    <div className="font-display text-3xl" style={{ color: GOLD }}>
                      {formatTry(price)}
                    </div>
                  ) : (
                    <div
                      className="inline-block rounded-full px-4 py-1.5 font-display text-base"
                      style={{ backgroundColor: "rgba(212,175,55,0.10)", color: GOLD, border: "1px solid rgba(212,175,55,0.25)" }}
                    >
                      Ücretsiz
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    onClick={handlePurchase}
                    className="h-12 rounded-xl px-8 font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                    style={{ backgroundColor: GOLD }}
                  >
                    {inCart ? "Sepeti Görüntüle" : price > 0 ? `Sepete Ekle — ${formatTry(price)}` : "Uygulamada Aç"}
                  </Button>

                  <Button
                    asChild
                    variant="outline"
                    className="h-12 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                  >
                    <Link to="/numeroloji/magaza">Mağazaya dön</Link>
                  </Button>
                </div>

                <p className="mt-4 text-[11px] text-slate-500">
                  İçeriğe Soul Circles mobil uygulamasından erişebilirsin. Satın alma işlemi anında hesabına tanımlanır.
                </p>
              </div>
            </section>

            {/* Info section */}
            <section className="mt-14">
              <div
                className="mx-auto overflow-hidden rounded-xl border px-6 py-5"
                style={{
                  borderColor: "rgba(212,175,55,0.12)",
                  background: "linear-gradient(145deg, #151518, #0a0a0c)",
                }}
              >
                <h2 className="font-display text-base tracking-wide text-slate-100">Erişim ve Teslimat</h2>
                <div className="mt-4 grid gap-3 text-sm text-slate-300/90">
                  <div>1. Satın al butonuna tıkla ve ödeme bilgilerini gir.</div>
                  <div>2. Satın alma anında Soul Circles hesabına tanımlanır.</div>
                  <div>3. Uygulamayı aç, Ritüeller sekmesinde içeriğin kilidini açık bulursun.</div>
                </div>
              </div>
            </section>

            {/* Reviews section */}
            <section id="yorumlar" className="mt-14">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-display text-2xl tracking-tight" style={{ color: GOLD }}>
                    Kullanıcı Yorumları
                  </h2>
                  {ratingAvg > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <Stars rating={Math.round(ratingAvg)} />
                      <span className="text-sm text-slate-400">
                        {ratingAvg.toFixed(1)} / 5 · {reviews.length} değerlendirme
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                  onClick={() => document.getElementById("yorum-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  <MessageSquarePlus className="mr-2 h-4 w-4" style={{ color: GOLD }} />
                  Yorum Yap
                </Button>
              </div>

              {/* Review cards */}
              {reviewsQuery.isLoading ? (
                <div className="mt-8 text-center text-sm text-slate-500">Yorumlar yükleniyor...</div>
              ) : (
                <div className="mt-8 grid gap-6 md:grid-cols-3">
                  {reviews.map((r) => (
                    <div
                      key={r.id}
                      className="rounded-xl border p-5"
                      style={{
                        background: "linear-gradient(145deg, #151518, #0a0a0c)",
                        borderColor: "rgba(212,175,55,0.12)",
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="grid h-10 w-10 shrink-0 place-items-center rounded-full border"
                          style={{ borderColor: "rgba(212,175,55,0.18)", backgroundColor: "rgba(212,175,55,0.08)" }}
                        >
                          <span className="text-sm font-semibold" style={{ color: GOLD }}>
                            {(r.display_name || "?").slice(0, 1).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-white">
                            {r.display_name || "Anonim"}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <Stars rating={r.rating} />
                            <span className="text-[11px] text-slate-500">{formatDate(r.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-slate-200/90">"{r.comment}"</p>
                    </div>
                  ))}

                  {reviews.length === 0 && !reviewsQuery.isLoading && (
                    <div
                      className="rounded-xl border p-8 text-center text-sm text-slate-400 md:col-span-3"
                      style={{ borderColor: "rgba(212,175,55,0.12)" }}
                    >
                      Henüz onaylı yorum yok. İlk yorumu sen bırak.
                    </div>
                  )}
                </div>
              )}

              {/* Review form */}
              <div id="yorum-form" className="mx-auto mt-10 max-w-3xl">
                <div
                  className="rounded-xl border p-6"
                  style={{
                    borderColor: "rgba(212,175,55,0.12)",
                    background: "linear-gradient(145deg, #151518, #0a0a0c)",
                  }}
                >
                  <div className="font-display text-base tracking-wide text-white">Yorumunu bırak</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Yorumun yönetici onayından sonra yayına alınır.
                  </div>

                  <div className="mt-5 grid gap-4">
                    <div className="grid gap-2">
                      <label className="text-sm text-slate-300">Adın</label>
                      <input
                        type="text"
                        value={reviewName}
                        onChange={(e) => setReviewName(e.target.value)}
                        placeholder="Örn: Elif Y."
                        className="h-11 rounded-md bg-black/40 px-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-1 ring-transparent focus:ring-[rgba(212,175,55,0.35)]"
                      />
                    </div>

                    <div className="grid gap-2">
                      <label className="text-sm text-slate-300">Puan</label>
                      <Stars
                        rating={reviewRating}
                        interactive
                        onSelect={setReviewRating}
                        className="gap-1.5"
                      />
                    </div>

                    <div className="grid gap-2">
                      <label className="text-sm text-slate-300">Yorum</label>
                      <textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Deneyimini kısaca yaz..."
                        rows={4}
                        className="w-full rounded-md bg-black/40 p-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-1 ring-transparent focus:ring-[rgba(212,175,55,0.35)]"
                      />
                    </div>

                    {reviewSent && reviewSent !== "ok" && (
                      <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                        {reviewSent}
                      </div>
                    )}

                    {reviewSent === "ok" && (
                      <div
                        className="rounded-lg border p-3 text-sm"
                        style={{ borderColor: "rgba(212,175,55,0.20)", backgroundColor: "rgba(212,175,55,0.10)", color: GOLD }}
                      >
                        Teşekkürler! Yorumun onaydan sonra yayına alınacak.
                      </div>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        onClick={handleSubmitReview}
                        disabled={!reviewComment.trim() || reviewMutation.isPending}
                        className="h-11 rounded-xl px-8 font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                        style={{ backgroundColor: GOLD }}
                      >
                        {reviewMutation.isPending ? "Gönderiliyor..." : "Gönder"}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                        onClick={() => {
                          setReviewName("");
                          setReviewComment("");
                          setReviewRating(5);
                          setReviewSent(null);
                        }}
                      >
                        Temizle
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
